// frontend/src/pages/admin/ProductCategories.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Badge, Select, Modal } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import './users.css';
import ProductCategoryForm from './ProductCategoryForm';
import { useNotificationApi } from '../../context/NotificationContext';
import dayjs from 'dayjs'; // Added dayjs import

const { Search } = Input;
const { Option } = Select;

const ProductCategories = () => {
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProductCategory, setEditingProductCategory] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [softDeletingId, setSoftDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchProductCategories = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchProductCategoriesLoading';
    const hideLoading = showLoading(loadingKey, 'Loading product categories...');

    try {
      const params = {
        status: filterStatus,
        sort: sortColumn,
        order: sortOrder,
      };

      if (searchText) {
        params.search = searchText;
      }

      const response = await api.get('/api/product-categories', { params });
      let fetchedCategories = response.data.data || [];

      if (!Array.isArray(fetchedCategories)) {
        console.warn('ProductCategories.js: API response for product categories was not an array. Assuming empty array.');
        fetchedCategories = [];
      }

      // Client-side sorting as fallback
      fetchedCategories.sort((a, b) => {
        if (sortColumn === 'createdAt') {
          const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
          const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
          return sortOrder === 'DESC' ? dateB - dateA : dateA - dateB;
        }
        if (sortColumn === 'name' || sortColumn === 'code') {
          return sortOrder === 'DESC' 
            ? (b[sortColumn] || '').localeCompare(a[sortColumn] || '')
            : (a[sortColumn] || '').localeCompare(b[sortColumn] || '');
        }
        return sortOrder === 'DESC' 
          ? (b[sortColumn] || 0) - (a[sortColumn] || 0)
          : (a[sortColumn] || 0) - (b[sortColumn] || 0);
      });

      setProductCategories(fetchedCategories);
      hideLoading();

      if (!triggeredByAction) {
        showSuccess('Product Categories Loaded', 'Product category list fetched successfully!', 'fetchProductCategoriesSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching product categories:', error.response?.data || error.message, error);
      showError('Failed to Fetch Product Categories', error.response?.data?.message || 'An unexpected error occurred while fetching product categories.', 'fetchProductCategoriesError');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchText, sortColumn, sortOrder, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchProductCategories();
  }, [fetchProductCategories]);

  const handleAddProductCategory = () => {
    setEditingProductCategory(null);
    setIsModalVisible(true);
  };

  const handleEditProductCategory = (categoryRecord) => {
    console.log('ProductCategories.js: handleEditProductCategory triggered. Category record received:', categoryRecord);
    setEditingProductCategory(categoryRecord);
    setIsModalVisible(true);
  };

  const handleDeactivateProductCategory = async (categoryId, categoryName) => {
    setDeactivatingId(categoryId);
    const loadingKey = `productCategoryStatusUpdate-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating ${categoryName}...`);

    try {
      await api.patch(`/api/product-categories/${categoryId}/deactivate`);
      hideLoading();
      showSuccess('Category Status Updated', `${categoryName} was deactivated.`, 'productCategoryStatusUpdateSuccess');
      fetchProductCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error deactivating product category:', error.response?.data || error.message, error);
      showError('Cannot Deactivate Category', error.response?.data?.message || `Failed to deactivate ${categoryName}.`, `deactivateProductCategoryError-${categoryId}`);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleActivateProductCategory = async (categoryId, categoryName) => {
    setActivatingId(categoryId);
    const loadingKey = `productCategoryStatusUpdate-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Activating ${categoryName}...`);

    try {
      await api.patch(`/api/product-categories/${categoryId}/activate`);
      hideLoading();
      showSuccess('Category Status Updated', `${categoryName} was activated.`, 'productCategoryStatusUpdateSuccess');
      fetchProductCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error activating product category:', error.response?.data || error.message, error);
      showError('Cannot Activate Category', error.response?.data?.message || `Failed to activate ${categoryName}.`, `activateProductCategoryError-${categoryId}`);
    } finally {
      setActivatingId(null);
    }
  };

  const handleSoftDeleteProductCategory = async (categoryId, categoryName) => {
    setSoftDeletingId(categoryId);
    const loadingKey = `productCategorySoftDelete-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Soft deleting ${categoryName}...`);

    try {
      await api.patch(`/api/product-categories/${categoryId}/soft-delete`);
      hideLoading();
      showSuccess('Category Soft Deleted', `Product category "${categoryName}" soft deleted successfully!`, 'productCategorySoftDeleteSuccess');
      fetchProductCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error soft deleting product category:', error.response?.data || error.message, error);
      showError('Cannot Soft Delete Category', error.response?.data?.message || `Failed to soft delete product category "${categoryName}".`, `softDeleteProductCategoryError-${categoryId}`);
    } finally {
      setSoftDeletingId(null);
    }
  };

  const handleRestoreProductCategory = async (categoryId, categoryName) => {
    setRestoringId(categoryId);
    const loadingKey = `productCategoryRestore-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Restoring ${categoryName}...`);

    try {
      await api.patch(`/api/product-categories/${categoryId}/restore`);
      hideLoading();
      showSuccess('Category Restored', `Product category "${categoryName}" restored successfully!`, 'productCategoryRestoreSuccess');
      fetchProductCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error restoring product category:', error.response?.data || error.message, error);
      showError('Cannot Restore Category', error.response?.data?.message || `Failed to restore product category "${categoryName}".`, `restoreProductCategoryError-${categoryId}`);
    } finally {
      setRestoringId(null);
    }
  };

  const handleHardDeleteProductCategory = async (categoryId, categoryName) => {
    setHardDeletingId(categoryId);
    const loadingKey = `productCategoryHardDelete-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Permanently deleting ${categoryName}...`);

    try {
      await api.delete(`/api/product-categories/${categoryId}`);
      hideLoading();
      showSuccess('Category Deleted', `Product category "${categoryName}" permanently deleted successfully!`, 'productCategoryHardDeleteSuccess');
      fetchProductCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error hard deleting product category:', error.response?.data || error.message, error);
      showError('Cannot Delete Category', error.response?.data?.message || `Failed to hard delete product category "${categoryName}".`, `hardDeleteProductCategoryError-${categoryId}`);
    } finally {
      setHardDeletingId(null);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (currentSorter && currentSorter.field) {
      const order = currentSorter.order === 'ascend' ? 'ASC' : 'DESC';
      setSortColumn(currentSorter.field);
      setSortOrder(order);
    } else {
      setSortColumn('createdAt');
      setSortOrder('DESC');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: {
        compare: (a, b) => a.id - b.id,
        multiple: 1,
      },
      defaultSortOrder: 'descend',
      width: '8%',
    },
    {
      title: 'Category Name',
      dataIndex: 'name',
      key: 'name',
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
        multiple: 2,
      },
      width: '20%',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: {
        compare: (a, b) => (a.code || '').localeCompare(b.code || ''),
        multiple: 3,
      },
      width: '10%',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || 'N/A',
      width: '20%',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <>
          {record.deletedAt ? (
            <Badge status="default" text="Deleted" />
          ) : (
            <Badge
              status={isActive ? 'success' : 'error'}
              text={isActive ? 'Active' : 'Inactive'}
            />
          )}
        </>
      ),
      width: '10%',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : 'N/A',
      sorter: {
        compare: (a, b) => {
          const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
          const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
          return dateA - dateB;
        },
        multiple: 4,
      },
      width: '15%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '17%',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditProductCategory(record)}
            aria-label={`Edit ${record.name}`}
            disabled={record.deletedAt}
          />

          {record.deletedAt ? (
            <Popconfirm
              title={`Are you sure you want to restore "${record.name}"?`}
              onConfirm={() => handleRestoreProductCategory(record.id, record.name)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: restoringId === record.id }}
            >
              <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Restore ${record.name}`} />
            </Popconfirm>
          ) : (
            <>
              {record.isActive ? (
                <Popconfirm
                  title={`Are you sure you want to deactivate "${record.name}"?`}
                  onConfirm={() => handleDeactivateProductCategory(record.id, record.name)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ loading: deactivatingId === record.id, danger: true }}
                >
                  <Button type="link" danger icon={<CloseCircleOutlined />} aria-label={`Deactivate ${record.name}`} />
                </Popconfirm>
              ) : (
                <Popconfirm
                  title={`Are you sure you want to activate "${record.name}"?`}
                  onConfirm={() => handleActivateProductCategory(record.id, record.name)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ loading: activatingId === record.id }}
                >
                  <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Activate ${record.name}`} />
                </Popconfirm>
              )}
              <Popconfirm
                title={`Are you sure you want to soft delete "${record.name}"? This will also deactivate it.`}
                onConfirm={() => handleSoftDeleteProductCategory(record.id, record.name)}
                okText="Yes, Soft Delete"
                cancelText="No"
                okButtonProps={{ danger: true, loading: softDeletingId === record.id }}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Soft Delete ${record.name}`}
                />
              </Popconfirm>
            </>
          )}

          <Popconfirm
            title={`Are you absolutely sure you want to PERMANENTLY DELETE the category "${record.name}"? This cannot be undone and will fail if products are linked. Only use if no longer needed ANYWHERE.`}
            onConfirm={() => handleHardDeleteProductCategory(record.id, record.name)}
            okText="Yes, Delete Permanently"
            cancelText="No, Keep It"
            okButtonProps={{ danger: true, loading: hardDeletingId === record.id }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Hard Delete ${record.name}`}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="product-categories-page">
      <div className="product-categories-header">
        <h2>Product Category Management</h2>
        <div className="product-categories-actions">
          <Space size="middle">
            <Search
              placeholder="Search categories..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchProductCategories()}
              style={{ width: 300 }}
            />
            <Select
              defaultValue="active"
              size="large"
              style={{ width: 150 }}
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                fetchProductCategories();
              }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="all">All Status (Incl. Deleted)</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddProductCategory}
              size="large"
            >
              Add Category
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={productCategories}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
        onChange={handleTableChange}
      />

      <ProductCategoryForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => fetchProductCategories(true)}
        productCategory={editingProductCategory}
      />
    </div>
  );
};

export default ProductCategories;